import { useState } from "react";
import { Handshake, HandHelping, Plus, Search } from "lucide-react";
import type { ContactDetail, MatchCard } from "@hub/shared";
import { Button, Pill } from "@/ui";
import { useMatchCards } from "./lib/hooks";
import { ItemForm, type ItemKind } from "./components/ItemForm";
import { MatchModal } from "./components/MatchModal";
import s from "./network.module.css";

/** Pure: counts how many people can help this contact and how many this contact can help, from the cached cards. */
export function helpCounts(cards: MatchCard[], contactId: string): { canHelpThem: number; theyCanHelp: number; waiting: MatchCard[] } {
  const active = cards.filter((c) => c.section !== "completed" && c.status !== "declined" && c.status !== "dismissed_by_owner" && c.status !== "dismissed_by_needer" && c.status !== "expired");
  const asNeeder = active.filter((c) => c.needer.contactId === contactId);
  const asHelper = active.filter((c) => c.helper.contactId === contactId);
  const helpers = new Set(asNeeder.map((c) => c.helper.contactId ?? c.helper.userId ?? c.matchId));
  const needers = new Set(asHelper.map((c) => c.needer.contactId ?? c.needer.userId ?? c.matchId));
  return { canHelpThem: helpers.size, theyCanHelp: needers.size, waiting: [...asNeeder, ...asHelper].sort((a, b) => b.score - a.score) };
}

/**
 * Overview strip under Highlights: this person's active needs and offers as chips, how the network can help, and the
 * two add buttons. "Suggest an intro" opens the best match.
 */
export function ContactNeedsOffersStrip({ detail }: { detail: ContactDetail }) {
  const { contact, profile, needs, services } = detail;
  const cards = useMatchCards();
  const [form, setForm] = useState<ItemKind | null>(null);
  const [open, setOpen] = useState<MatchCard | null>(null);
  const activeNeeds = needs.filter((n) => !n.isCompleted);
  const activeServices = services.filter((sv) => sv.isActive);
  const counts = helpCounts(cards.data?.cards ?? [], contact.id);
  const name = profile.displayName;
  return (
    <section className={s.block} style={{ marginBottom: 14 }} data-testid="needs-offers-strip">
      <div className="u-row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <span className="u-row" style={{ gap: 6, fontWeight: 600 }}><Handshake size={14} /> Needs and offers</span>
        <span className="u-row" style={{ gap: 6, flexWrap: "wrap" }}>
          <Button size="sm" variant="ghost" icon={<Plus size={13} />} onClick={() => setForm("need")}>Add a need</Button>
          <Button size="sm" variant="ghost" icon={<Plus size={13} />} onClick={() => setForm("service")}>Add an offer</Button>
          {counts.waiting.length > 0 && (
            <Button size="sm" variant="primary" icon={<HandHelping size={13} />} onClick={() => setOpen(counts.waiting[0])} data-testid="suggest-intro">
              Suggest an intro
            </Button>
          )}
        </span>
      </div>
      <div className="u-col" style={{ gap: 6, marginTop: 8 }}>
        <div className="u-row" style={{ gap: 6, flexWrap: "wrap" }}>
          <span className={`${s.small} ${s.muted}`} style={{ minWidth: 72 }}>Looking for</span>
          {activeNeeds.length === 0 && <span className={`${s.small} ${s.muted}`}>Nothing recorded yet.</span>}
          {activeNeeds.map((n) => <Pill key={n.id} tone="warning" title={n.description || n.category}><Search size={10} /> {n.title}</Pill>)}
        </div>
        <div className="u-row" style={{ gap: 6, flexWrap: "wrap" }}>
          <span className={`${s.small} ${s.muted}`} style={{ minWidth: 72 }}>Can help with</span>
          {activeServices.length === 0 && <span className={`${s.small} ${s.muted}`}>Nothing recorded yet.</span>}
          {activeServices.map((sv) => <Pill key={sv.id} tone="success" title={sv.shortDescription || sv.category}><HandHelping size={10} /> {sv.title}</Pill>)}
        </div>
        <div className={`${s.small} ${s.muted}`} data-testid="help-counts">
          {counts.canHelpThem > 0 && <span>{counts.canHelpThem === 1 ? "1 person" : `${counts.canHelpThem} people`} in your network can help {name}. </span>}
          {counts.theyCanHelp > 0 && <span>Can help {counts.theyCanHelp === 1 ? "1 person" : `${counts.theyCanHelp} people`} you know. </span>}
          {counts.canHelpThem === 0 && counts.theyCanHelp === 0 && (activeNeeds.length + activeServices.length === 0 ? <span>Hub matches needs with offers across your network. Record what {name} needs or can do and matches appear here.</span> : <span>No matches yet. Matches are found when you refresh Introductions or when your network changes.</span>)}
        </div>
      </div>
      {form && <ItemForm kind={form} contactId={contact.id} open forLabel={name} onClose={() => setForm(null)} />}
      <MatchModal card={open} onClose={() => setOpen(null)} />
    </section>
  );
}
